import React from 'react';
import { Container } from 'components/UI/Container/Container';
import { PrismicLink } from 'components/PrismicLink';
import { LrLogo } from 'components/Icons/LrLogo';
import { MailchimpSubscribe } from 'components/MailchimpSubscribe';
import { LogoFacebook } from 'components/Icons/LogoFacebook';
import { LogoInstagram } from 'components/Icons/LogoInstagram';
import { LogoSpotify } from 'components/Icons/LogoSpotify';
import { LogoTwitter } from 'components/Icons/LogoTwitter';
import { LogoYoutube } from 'components/Icons/LogoYoutube';
import { Grid, GridRow, GridCol } from 'components/UI/Grid';
import { AlternateLanguage } from 'types';
import { homepageLink } from 'helpers';
import { MainFooterColumn } from './MainFooterColumn';
import { LanguageSelector } from './LanguageSelector';
import './MainFooter.scss';

type FooterItem = {
    label: string;
    link: {
        url: string;
        raw?: {
            target: '_blank' | null;
        };
    };
};

export type MainFooterProps = {
    lang: string;
    alternateLanguages: AlternateLanguage[];
    mainFooterData: {
        copyright_info: string;
        privacy_policy_label: string;
        privacy_policy_link: {
            url: string;
        };
        tos_label: string;
        tos_link: {
            url: string;
        };
        social_items: FooterItem[];
        products_title: string;
        products_items: FooterItem[];
        landr_title: string;
        landr_items: FooterItem[];
        resources_title: string;
        resources_items: FooterItem[];
        newsletter_title: string;
        newsletter_description: {
            html: string;
            text: string;
        };
        newsletter_email_placeholder: string;
        newsletter_cta: string;
    };
};

const MainFooter = (props: MainFooterProps) => {
    const data = props.mainFooterData;
    return (
        <Container size="md">
            <Grid className="MainFooter">
                <GridRow
                    horizontalAlign={'h-start'}
                    verticalAlign={'v-stretch'}
                    className="MainFooter-columns"
                >
                    <MainFooterColumn
                        title={data.products_title}
                        items={data.products_items}
                        colNum={'3'}
                    />

                    <MainFooterColumn
                        title={data.landr_title}
                        items={data.landr_items}
                        colNum={'2'}
                    />

                    <MainFooterColumn
                        title={data.resources_title}
                        items={data.resources_items}
                        colNum={'3'}
                    />

                    <MainFooterColumn title={data.newsletter_title} colNum={'4'} shrink={true}>
                        <label
                            className="MainFooter-column-item"
                            htmlFor="footerMailchimpSubscribe"
                        >
                            {data.newsletter_description.text}
                        </label>
                        <MailchimpSubscribe
                            id="footerMailchimpSubscribe"
                            placeholder={data.newsletter_email_placeholder}
                            cta={data.newsletter_cta}
                        />
                    </MainFooterColumn>
                </GridRow>
                <GridRow
                    horizontalAlign={'h-start'}
                    verticalAlign={'v-stretch'}
                    className="MainFooter-bottom"
                >
                    <GridCol
                        colSize={`7`}
                        offsetSize={`0`}
                        mobileColSize={'12'}
                        className="MainFooter-bottom-left"
                    >
                        <PrismicLink link={homepageLink(props.lang)} className="MainFooter-logo">
                            <LrLogo />
                        </PrismicLink>
                        <span className="MainFooter-link">{data.copyright_info}</span>
                        <PrismicLink
                            label={data.privacy_policy_label}
                            link={data.privacy_policy_link}
                            className="MainFooter-link u-hidden-lg-down"
                        />
                        <PrismicLink
                            label={data.tos_label}
                            link={data.tos_link}
                            className="MainFooter-link u-hidden-lg-down"
                        />
                    </GridCol>

                    <GridCol
                        colSize={`5`}
                        offsetSize={`0`}
                        mobileColSize={'12'}
                        className="MainFooter-bottom-left Right-side"
                    >
                        <div className="MainFooter-bottom-icons">
                            {data.social_items.map((item, index) => (
                                <PrismicLink
                                    key={index}
                                    className={`MainFooter-link Icon ${item.label}`}
                                    link={item.link}
                                >
                                    {/* TODO CREATE ICON LIST COMPONENT */}

                                    {(() => {
                                        switch (item.label) {
                                            case 'Facebook':
                                                return <LogoFacebook />;
                                            case 'Instagram':
                                                return <LogoInstagram />;
                                            case 'Spotify':
                                                return <LogoSpotify />;
                                            case 'Twitter':
                                                return <LogoTwitter />;
                                            case 'Youtube':
                                                return <LogoYoutube />;
                                        }
                                    })()}
                                </PrismicLink>
                            ))}
                        </div>
                        <LanguageSelector />
                    </GridCol>
                </GridRow>
            </Grid>
        </Container>
    );
};

export { FooterItem, MainFooter };
